import * as React from 'react';
// form
import { useFormContext, Controller } from 'react-hook-form';
import { cn } from '../../lib/utils';
import { CLASSES_ENTRADA, ErroCampo, sxParaEstilo } from './RHFTextField';

/* ============================================================================
   Select do visual novo (<select> nativo, sem MUI).

   O PROBLEMA CENTRAL DESTE ARQUIVO: os seis formularios publicados passam
   <MenuItem> do MUI como children -

       <RHFSelect name="status" required slotProps={...}>
         {[{label:'ATIVO', value:1}, {label:'INATIVO', value:0}].map((o) => (
           <MenuItem key={o.value} value={o.value} sx={{...}}>{o.label}</MenuItem>
         ))}
       </RHFSelect>

   Um <select> nativo nao aceita <MenuItem> como filho (o navegador descarta
   qualquer elemento que nao seja <option>/<optgroup>, e a lista aparece vazia).
   Como a instrucao e nao obrigar mudanca em quem chama, este componente LE os
   children com React.Children.toArray, extrai de cada filho a prop `value` e o
   texto, e gera as <option> ele mesmo. Os <MenuItem> nunca sao renderizados -
   servem apenas de descricao das opcoes.

   POR QUE A OPTION CARREGA O INDICE E NAO O VALOR: o atributo `value` do DOM e
   sempre STRING. As telas usam valores NUMERICOS (`value={1}` para ATIVO), e o
   backend recebe esse campo como numero. Se a option levasse o valor, o
   `onChange` devolveria a string "1" e o formulario passaria a gravar "1" no
   lugar de 1 - mudanca de contrato silenciosa, do tipo que so aparece em
   producao. Levando o INDICE, o valor original (numero, string, o que for) e
   devolvido intacto do array de opcoes.

   DEFEITO CORRIGIDO - "mostra ATIVO e salva vazio": quando o valor do formulario
   nao casa com nenhuma opcao (cadastro novo, campo ainda vazio), o navegador
   exibe a PRIMEIRA opcao mesmo com o formulario vazio. O usuario le "ATIVO" na
   tela, nao mexe no campo, envia, e a validacao acusa campo obrigatorio sem que
   nada pareca errado. Agora, nesse caso, entra uma opcao "Selecione..." como
   selecionada, deixando o estado visivel. Nada muda no dado enviado.
   ============================================================================ */

/** Reduz o children de um MenuItem a texto puro para virar rotulo da <option>. */
function textoDoNo(no) {
  if (no === null || no === undefined || typeof no === 'boolean') return '';
  if (typeof no === 'string' || typeof no === 'number') return String(no);
  if (Array.isArray(no)) return no.map(textoDoNo).join('');
  if (React.isValidElement(no)) return textoDoNo(no.props?.children);
  return '';
}

/**
 * Achata os children em [{ valor, rotulo, desabilitado }].
 *
 * React.Children.toArray ja resolve arrays aninhados e fragmentos (o padrao dos
 * chamadores e `{lista.map(...)}`), e descarta null/false - o que cobre as telas
 * que fazem `{condicao && <MenuItem .../>}`.
 */
function opcoesDosChildren(children) {
  return React.Children.toArray(children)
    .filter((filho) => React.isValidElement(filho))
    .map((filho) => ({
      valor: filho.props?.value,
      rotulo: textoDoNo(filho.props?.children),
      desabilitado: Boolean(filho.props?.disabled),
    }));
}

// `@type {any}`: com `React.forwardRef` o TypeScript nao consegue inferir o tipo
// das props a partir do padrao de desestruturacao, e assume props vazias - o que
// faz TODO chamador .tsx quebrar em `name`, `fullWidth` etc. A versao anterior
// (MUI, `{ name, ...other }`) aceitava qualquer prop; isto preserva o mesmo
// contrato sem mexer nos chamadores.
/** @type {any} */
const RHFSelect = React.forwardRef(function RHFSelect(
  {
    name,
    children,
    // `labelIcon` e o formato das telas antigas: [rotulo, dica do Google]. Fica
    // sendo o rotulo do campo, com a dica no title de um icone de ajuda.
    // O default de antes era a expressao `null || '' || {} || object || ...`, que
    // sempre resolve para {} - mantido o efeito pratico (nenhum rotulo), agora
    // escrito de forma legivel.
    labelIcon = null,
    label,
    required,
    id,
    sx,
    className,
    disabled,
    // Aceitas e descartadas (aparencia do MUI); nomeadas para nao vazarem ao DOM.
    fullWidth,
    size,
    variant,
    margin,
    color,
    slotProps,
    SelectProps,
    InputProps,
    InputLabelProps,
    helperText,
    native,
    ...resto
  },
  ref
) {
  const { control } = useFormContext();
  const idCampo = id || `campo-${name}`;
  const idErro = `${idCampo}-erro`;

  const opcoes = React.useMemo(() => opcoesDosChildren(children), [children]);

  const temLabelIcon = Array.isArray(labelIcon) && labelIcon.length > 0;
  const rotulo = temLabelIcon ? labelIcon[0] : label;
  const dica = temLabelIcon ? labelIcon[1] : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // Comparacao por String porque o valor do formulario pode chegar como
        // numero (1) e a opcao como string ("1") - vindo de tela ou de banco.
        const indice = opcoes.findIndex((op) => String(op.valor) === String(field.value));
        const semCasar = indice < 0;

        return (
          <div className={cn('w-full', className)} style={sxParaEstilo(sx)}>
            {rotulo && (
              <label htmlFor={idCampo} className="mb-1 flex items-center gap-1 text-[12px] font-semibold text-ink">
                <span>{rotulo}</span>
                {required && <span className="text-danger">*</span>}
                {dica && (
                  // <span> e nao <button>: botao dentro de <label> faz o clique
                  // no rotulo disparar o botao tambem. O title basta para a dica,
                  // que era exatamente o papel do Tooltip do MUI aqui.
                  <span
                    title={dica}
                    aria-label={dica}
                    className="grid h-4 w-4 flex-none cursor-help place-items-center rounded-full bg-info-soft text-[10px] font-bold text-info"
                  >
                    ?
                  </span>
                )}
              </label>
            )}

            <select
              {...resto}
              // Dois refs no mesmo no: o do react-hook-form (usado por ele para
              // dar foco no campo que falhou na validacao) e o externo, que o
              // Tooltip do MUI injeta ao clonar o filho. Sem juntar os dois, um
              // dos dois recursos para de funcionar em silencio.
              ref={(no) => {
                field.ref(no);
                if (typeof ref === 'function') ref(no);
                else if (ref) ref.current = no;
              }}
              id={idCampo}
              name={field.name}
              onBlur={field.onBlur}
              disabled={Boolean(disabled)}
              // `aria-required` e NAO o atributo `required` do HTML. Este e o
              // unico componente do lote que recebe `required` de verdade dos
              // chamadores (cinco telas passam), e com `required` no <select> o
              // navegador barraria o submit com a bolha nativa ANTES de o Yup
              // rodar - trocando a mensagem do projeto pela do navegador e
              // mudando a ordem de tratamento de erro. Com o MUI isso nunca
              // acontecia porque o <select> nunca ficava com valor vazio (ver a
              // nota do "Selecione..." no topo). O asterisco no rotulo continua
              // sinalizando a obrigatoriedade.
              aria-required={Boolean(required)}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? idErro : undefined}
              value={semCasar ? '' : String(indice)}
              onChange={(e) => {
                const escolhido = opcoes[Number(e.target.value)];
                // Devolve o valor ORIGINAL da opcao (numero continua numero).
                field.onChange(escolhido ? escolhido.valor : '');
              }}
              className={cn(CLASSES_ENTRADA, 'cursor-pointer capitalize', error && 'border-danger')}
            >
              {semCasar && (
                <option value="" disabled>
                  Selecione...
                </option>
              )}
              {opcoes.map((op, i) => (
                <option key={`${op.valor}-${i}`} value={String(i)} disabled={op.desabilitado}>
                  {op.rotulo}
                </option>
              ))}
            </select>

            <ErroCampo id={idErro}>{error?.message}</ErroCampo>
          </div>
        );
      }}
    />
  );
});

export default RHFSelect;
